import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  Grid,
  Card,
  Avatar,
  useTheme,
  useMediaQuery,
  Slide,
  Chip
} from '@mui/material';
import {
  Close,
  Print,
  MonetizationOn,
  CreditCard,
  QrCode,
  CheckCircle
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { gradients } from '../styles/posStyles';

// Create a transition component for the dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PaymentDialog = ({
  open,
  onClose,
  totalAmount,
  selectedPaymentMethod,
  processOrder
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const MotionDiv = motion.div;
  
  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Handle dialog close with proper cleanup
  const handleClose = (event, reason) => {
    // Prevent closing on backdrop click if needed
    if (reason === 'backdropClick') {
      return;
    }
    
    // Clear any focused elements
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    onClose(event, reason);
  };

  // Payment method configurations
  const paymentMethods = [
    {
      id: 'cash',
      icon: MonetizationOn,
      label: t('pos.cash'),
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      available: true,
      description: t('pos.dialog.cashDescription') || 'Pay with cash'
    },
    {
      id: 'card',
      icon: CreditCard,
      label: t('pos.card'),
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      available: false,
      description: t('pos.dialog.cardDescription') || 'Credit/Debit Card'
    },
    {
      id: 'qr',
      icon: QrCode,
      label: t('pos.qrPay'),
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      available: false,
      description: t('pos.dialog.qrDescription') || 'Scan QR Code'
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isMobile ? 'xs' : 'sm'}
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition}
      TransitionProps={{
        onExited: () => {
          if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
          }
        }
      }}
      disableRestoreFocus={true}
      disableAutoFocus={false}
      disableEnforceFocus={false}
      keepMounted={false}
      PaperProps={{
        sx: { 
          borderRadius: isMobile ? 0 : 4,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          '&:focus': {
            outline: 'none'
          }
        }
      }}
      aria-labelledby="payment-dialog-title"
      aria-describedby="payment-dialog-description"
    >
      {/* Modern Header with Gradient */}
      <Box sx={{ 
        background: gradients.primary,
        p: isMobile ? 2 : 3,
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)'
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            id="payment-dialog-title"
            variant={isMobile ? "h6" : "h5"} 
            sx={{ 
              fontWeight: 700,
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            {t('pos.dialog.paymentTitle')}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              mt: 0.5,
              fontSize: isMobile ? '0.8rem' : '0.875rem'
            }}
          >
            {t('pos.dialog.selectPaymentMethod')}
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose}
          sx={{ 
            position: 'relative',
            zIndex: 1,
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease'
          }}
          size={isMobile ? "small" : "medium"}
        >
          <Close />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ 
        p: isMobile ? 2 : 3,
        background: 'transparent'
      }}>
        {/* Amount Display with Glass Effect */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            textAlign: 'center',
            mb: isMobile ? 3 : 4,
            p: isMobile ? 2 : 3,
            background: 'rgba(102, 126, 234, 0.08)',
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            border: '1px solid rgba(102, 126, 234, 0.1)'
          }}>
            <Typography 
              id="payment-dialog-description"
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1,
                fontSize: isMobile ? '0.8rem' : '0.875rem'
              }}
            >
              {t('pos.dialog.totalAmount')}
            </Typography>
            <Typography 
              variant={isMobile ? "h3" : "h2"} 
              sx={{ 
                fontWeight: 800,
                color: '#667eea',
                textShadow: '0 2px 4px rgba(102, 126, 234, 0.2)',
                fontSize: isMobile ? '2rem' : isTablet ? '2.5rem' : '3rem'
              }}
            >
              ${totalAmount.toFixed(2)}
            </Typography>
          </Box>
        </MotionDiv>
        
        {/* Payment Methods Grid */}
        <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
          {paymentMethods.map((method, index) => (
            <Grid item xs={12} sm={4} key={method.id}>
              <MotionDiv
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: method.available ? 1.05 : 1 }}
                whileTap={{ scale: method.available ? 0.95 : 1 }}
              >
                <Card 
                  sx={{ 
                    height: isMobile ? 140 : 160,
                    cursor: method.available ? 'pointer' : 'not-allowed',
                    borderRadius: 3,
                    background: method.available 
                      ? 'linear-gradient(135deg, #ffffff 0%, rgba(102, 126, 234, 0.05) 100%)'
                      : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: selectedPaymentMethod === method.id 
                      ? `3px solid #667eea` 
                      : '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: method.available 
                      ? '0 10px 30px -5px rgba(102, 126, 234, 0.3)'
                      : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    opacity: method.available ? 1 : 0.6,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': method.available ? {
                      boxShadow: '0 20px 40px -10px rgba(102, 126, 234, 0.4)',
                      transform: 'translateY(-2px)'
                    } : {},
                    '&::before': method.available ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: method.gradient,
                      opacity: 0.1,
                      transition: 'opacity 0.3s ease'
                    } : {}
                  }}
                  onClick={() => method.available && processOrder(method.id)}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    p: isMobile ? 1.5 : 2,
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <Avatar 
                      sx={{ 
                        background: method.available ? method.gradient : '#6c757d',
                        width: isMobile ? 48 : 56, 
                        height: isMobile ? 48 : 56,
                        mb: isMobile ? 1 : 1.5,
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      <method.icon fontSize={isMobile ? "medium" : "large"} />
                    </Avatar>
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      sx={{ 
                        fontWeight: 600,
                        color: method.available ? '#333' : '#6c757d',
                        mb: 0.5,
                        textAlign: 'center'
                      }}
                    >
                      {method.label}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: method.available ? '#666' : '#adb5bd',
                        textAlign: 'center',
                        fontSize: isMobile ? '0.7rem' : '0.75rem'
                      }}
                    >
                      {method.description}
                    </Typography>
                    
                    {!method.available && (
                      <Chip 
                        label={t('pos.dialog.comingSoon') || 'Coming Soon'}
                        size="small"
                        sx={{ 
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: '#ffc107',
                          color: '#333',
                          fontSize: '0.65rem',
                          fontWeight: 600
                        }} 
                      />
                    )}
                    
                    {selectedPaymentMethod === method.id && (
                      <CheckCircle 
                        sx={{ 
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: '#10b981',
                          fontSize: 20
                        }} 
                      />
                    )}
                  </Box>
                </Card>
              </MotionDiv>
            </Grid>
          ))}
        </Grid>
        
        <Divider sx={{ 
          my: isMobile ? 2 : 3,
          background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.3), transparent)'
        }} />
        
        {/* Action Buttons */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Box sx={{ display: 'flex', gap: isMobile ? 1.5 : 2, flexDirection: isMobile ? 'column' : 'row' }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              startIcon={<Close />}
              fullWidth={isMobile}
              sx={{ 
                borderRadius: 2,
                borderColor: '#e2e8f0',
                color: '#64748b',
                height: isMobile ? 48 : 52,
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {t('common.cancel')}
            </Button>
            
            <Button
              variant="contained"
              onClick={() => {
                // Show success message
                const successMessage = t('pos.dialog.receiptPrinted') || 'Receipt will be printed';
                // You can replace alert with a proper toast notification
                alert(successMessage);
                processOrder(selectedPaymentMethod || 'cash');
              }}
              startIcon={<Print />}
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
                background: gradients.primary,
                height: isMobile ? 48 : 52,
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontWeight: 600,
                boxShadow: '0 10px 20px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: gradients.primaryHover,
                  boxShadow: '0 15px 30px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(0px)'
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {t('pos.dialog.printReceipt')}
            </Button>
          </Box>
        </MotionDiv>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;